import type {
  PlaygroundConfig,
  PlaygroundConfigEnv,
  PlaygroundDefinition,
  PlaygroundInlineEntry,
  PlaygroundModule,
  PlaygroundRuntimeSelection,
  PlaygroundOptimizeDepsOptions,
  PlaygroundAliasEntry,
  PlaygroundMockMap,
  PlaygroundStubMap,
  PlaygroundModuleMockDescriptor,
  PlaygroundGameHooks
} from '../types.js';
import { DEFAULT_RENDER_BLACK_LIMIT } from '@gotti/test-utils';
import { unique } from '../util/arrays.js';
import { ensureBudgetMs, normalizePlaygroundTests } from './tests.js';
import { PLAYGROUND_PAIR } from './pair.js';
import { DEFINITION_KEYS, validateKnownKeys } from './validate.js';

/** `readyTimeoutMs` when a definition declares `ready` or `game` but no budget of its own. */
export const DEFAULT_READY_TIMEOUT_MS = 60_000;

/**
 * Refuses a declared `videoBlackLimit` (`what` names where it is declared) that is not a number above 0 and at most 1;
 * returns it as declared (`undefined` when it is not). A limit above 1 is refused, never clamped: no take would reach it,
 * so the `--video` gate would be off — and NaN fails every comparison the gate makes, which would do the same.
 */
export const checkVideoBlackLimit = (value: unknown, what: string): number | undefined => {
  if (value === undefined) return undefined;
  if (typeof value !== 'number' || !(value > 0 && value <= 1)) {
    throw new Error(
      `${what} must be a number above 0 and at most 1 — the black fraction of the judged area at which --video refuses a take ` +
        `(above 1 no take would ever be refused); got ${typeof value === 'string' ? JSON.stringify(value) : String(value)}.`,
    );
  }
  return value;
};

/** One entry of a config's `playgrounds` as declared: a glob/filename pattern, or an inline definition (or pair) by name. */
export type PlaygroundConfigEntry = { pattern: string } | { name: string; entry: unknown };

/**
 * A config's `playgrounds` as declared, in configured order: each glob/filename pattern, and each inline entry by its
 * name — the object form's entries by key, the array form's objects by their `name` (handed back without it). Pure and
 * browser-safe: the node loader lists every playground from it, and the page finds a `member` in it.
 */
export const playgroundEntries = (config: PlaygroundConfig): PlaygroundConfigEntry[] => {
  const playgrounds: unknown = config.playgrounds;
  if (playgrounds && typeof playgrounds === 'object' && !Array.isArray(playgrounds)) {
    return Object.entries(playgrounds).map(([name, entry]) => ({ name, entry }));
  }
  return (Array.isArray(playgrounds) ? playgrounds : [playgrounds]).map((element: unknown) => {
    if (typeof element === 'string') return { pattern: element };
    const { name, ...entry } = (element ?? {}) as { name?: string };
    if (!name) throw new Error('Playground definition in array is missing a "name" property.');
    return { name, entry };
  });
};

/**
 * The config a lone definition (or pair) file serves as when it is passed as the config: what the module declares,
 * its one playground being the module itself under `name`. The module's own `clientConfig` and `gameHooks` stay the
 * playground's alone, and so does the pair marker — kept on the config view too, the clientConfig would be layered
 * twice (a shared `domSetup` running twice per client) and the hooks composed twice.
 */
export const leafConfigView = (module: object, name: string): PlaygroundConfig => {
  const { clientConfig: _clientConfig, gameHooks: _gameHooks, [PLAYGROUND_PAIR]: _pair, ...view } = module as Record<PropertyKey, unknown>;
  return { ...view, playgrounds: { [name]: module as PlaygroundInlineEntry } } as PlaygroundConfig;
};

/**
 * Check if a stub value is a PlaygroundModuleMockDescriptor (has `module` property)
 */
const isModuleMockDescriptor = (value: unknown): value is PlaygroundModuleMockDescriptor => {
  return typeof value === 'object' && value !== null && 'module' in value && typeof (value as any).module === 'string';
};

/**
 * Parse a stubs map into separate aliases (for paths) and mocks (for inline objects)
 */
const parseStubs = (stubs?: PlaygroundStubMap): { aliases: PlaygroundAliasEntry[]; mocks: PlaygroundMockMap } => {
  const aliases: PlaygroundAliasEntry[] = [];
  const mocks: PlaygroundMockMap = {};

  if (!stubs) {
    return { aliases, mocks };
  }

  for (const [moduleName, stubValue] of Object.entries(stubs)) {
    if (typeof stubValue === 'string') {
      // String = path alias
      aliases.push({ find: moduleName, replacement: stubValue });
    } else if (isModuleMockDescriptor(stubValue)) {
      // PlaygroundModuleMockDescriptor = re-export from another module
      mocks[moduleName] = stubValue;
    } else {
      // Plain object = inline mock
      mocks[moduleName] = stubValue;
    }
  }

  return { aliases, mocks };
};

/**
 * Merge stubs from base and instance configs
 */
const mergeStubs = (
  baseStubs?: PlaygroundStubMap,
  instanceStubs?: PlaygroundStubMap
): { aliases: PlaygroundAliasEntry[]; mocks: PlaygroundMockMap } => {
  const baseParsed = parseStubs(baseStubs);
  const instanceParsed = parseStubs(instanceStubs);

  return {
    aliases: [...baseParsed.aliases, ...instanceParsed.aliases],
    mocks: { ...baseParsed.mocks, ...instanceParsed.mocks },
  };
};

const mergeOptimizeDeps = (
  base?: PlaygroundOptimizeDepsOptions,
  override?: PlaygroundOptimizeDepsOptions
): PlaygroundOptimizeDepsOptions => {
  return {
    include: unique([...(base?.include ?? []), ...(override?.include ?? [])]),
    exclude: unique([...(base?.exclude ?? []), ...(override?.exclude ?? [])]),
  };
};

const mergeHooks = (
  base?: PlaygroundGameHooks,
  override?: PlaygroundGameHooks
): PlaygroundGameHooks | undefined => {
  if (!base && !override) return undefined;
  if (!base) return override;
  if (!override) return base;

  const merged: PlaygroundGameHooks = {};

  for (const side of ['server', 'client'] as const) {
    const baseSide = base[side];
    const overrideSide = override[side];

    if (!baseSide && !overrideSide) continue;
    if (!baseSide) {
      merged[side] = overrideSide as any;
      continue;
    }
    if (!overrideSide) {
      merged[side] = baseSide as any;
      continue;
    }

    merged[side] = {} as any;
    for (const phase of ['onStart', 'onUpdate', 'onStop'] as const) {
      const baseFn = baseSide[phase];
      const overrideFn = overrideSide[phase];

      if (baseFn && overrideFn) {
        merged[side]![phase] = ((...args: any[]) => {
          (baseFn as any)(...args);
          (overrideFn as any)(...args);
        }) as any;
      } else {
        merged[side]![phase] = (baseFn || overrideFn) as any;
      }
    }
  }
  return merged;
};

/**
 * A config module as the config it holds: a default export unwrapped, a function form called — with `command: 'serve'`,
 * since the playground only ever serves, and `mode` from `NODE_ENV` (default `'development'`) — and its result
 * unwrapped again. Throws when that is no object.
 */
export const resolvePlaygroundModule = async (module: PlaygroundModule): Promise<PlaygroundConfig> => {
  const env: PlaygroundConfigEnv = {
    command: 'serve',
    mode: process.env.NODE_ENV || 'development',
  };

  let result: any = module;

  // Handle ES module default export
  if (result && typeof result === 'object' && 'default' in result) {
    result = result.default;
  }

  // Handle function config
  if (typeof result === 'function') {
    result = await result(env);
  }

  // Handle ES module default export again (in case function returned a module object)
  if (result && typeof result === 'object' && 'default' in result) {
    result = result.default;
  }

  if (!result || typeof result !== 'object') {
    throw new Error('Playground config module did not return an object.');
  }
  return result as PlaygroundConfig;
};

/**
 * One playground's definition merged with its config, as the runtime runs it. A key the definition type does not have
 * fails it here, naming the key, the playground and — on the node side — its file (`playgroundPath`; the page has none).
 */
export const selectPlaygroundRuntime = (
  config: PlaygroundConfig,
  definition: PlaygroundDefinition,
  playgroundName: string,
  playgroundPath: string
): PlaygroundRuntimeSelection => {
  validateKnownKeys(definition, DEFINITION_KEYS, `playground definition "${playgroundName}"${playgroundPath ? ` (${playgroundPath})` : ''}`);
  const parsedStubs = mergeStubs(config.stubs, definition.stubs);
  const mocks = parsedStubs.mocks;
  const stubAliases = parsedStubs.aliases;
  
  // The config's latencies are defaults: the definition's own win field by field.
  const mockConfig = { ...(config.mockConfig ?? {}), ...(definition.mockConfig ?? {}) };
  const gameHooks = mergeHooks(config.gameHooks, definition.gameHooks);
  
  const tests = normalizePlaygroundTests({
    definitions: definition.tests ? definition.tests : (definition.test ? [definition.test] : []),
    scenario: definition.scenario,
    selectionName: playgroundName,
  });

  const autoOpen =
    config.autoOpen ??
    true;

  const port =
    config.port ??
    5173;

  const host =
    config.host ??
    '127.0.0.1';

  const entry = definition.entry;
  const setup = definition.setup;

  const preload = unique([...(config.preload ?? []), ...(definition.preload ?? [])]);
  const globals = { ...(definition.globals ?? {}) };

  const readyTimeoutMs = ensureBudgetMs(
    definition.readyTimeoutMs,
    `readyTimeoutMs on playground "${playgroundName}"`,
    DEFAULT_READY_TIMEOUT_MS,
  );

  // Both are judged, though the definition's wins: a config's bad limit is wrong whichever playground reads it. (A
  // config loaded from a file was already judged there, naming the file — `loadPlaygroundConfigFromFile`.)
  const ownBlackLimit = checkVideoBlackLimit(
    definition.videoBlackLimit,
    `videoBlackLimit on playground "${playgroundName}"${playgroundPath ? ` (${playgroundPath})` : ''}`,
  );
  const configBlackLimit = checkVideoBlackLimit(config.videoBlackLimit, 'videoBlackLimit in the playground config');
  const videoBlackLimit = ownBlackLimit ?? configBlackLimit ?? DEFAULT_RENDER_BLACK_LIMIT;

  return {
    name: playgroundName,
    playgroundFilePath: playgroundPath,
    config,
    definition,
    environment: definition.environment,
    clientConfig: definition.clientConfig ?? {},
    clientConfigDefaults: config.clientConfig,
    mocks,
    stubAliases,
    mockConfig,
    autoOpen,
    port,
    host,
    entry,
    preload,
    globals,
    setup,
    gameHooks,
    tests,
    ready: definition.ready,
    readyTimeoutMs,
    game: definition.game,
    videoBlackLimit,
  };
};

export const collectOptimizeDeps = (
  config: PlaygroundConfig,
  definition: PlaygroundDefinition
): PlaygroundOptimizeDepsOptions => {
  return mergeOptimizeDeps(config.optimizeDeps, definition.optimizeDeps);
};
